import { FC, useState, useEffect, useRef, useMemo, useCallback } from "react";
import { Product } from "../types/product";
import { baseURL } from "../libs/http";
import Button from "./ui/Button";
import "./css/product.css";
import { isMobile } from "react-device-detect";
import { getDeclension } from "../utils/tools";
import _ from "lodash";
import appStore from "../stores/appStore";
import cartOutline from "../assets/cart_outline.svg";
import { Spinner } from "./ui/Spinner";
import accountStore from "../pages/AccountPage/store/accountStore";
import Modal from "./ui/Modal";
import { Select } from "./ui/Select";
import Counter from "./ui/Form/FormCounter";
import basketStore from "../pages/BasketPage/store/basketStore";
import { toast } from "react-toastify";
import { uniqueId } from "lodash";

interface IProductProps {
  product: Product;
  className: string;
}

const ProductCard: FC<IProductProps> = ({ product, className }) => {
  // Состояния
  const [imageUrl, setImageUrl] = useState<string>(
    "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 1 1'%3E%3C/svg%3E"
  );
  const [isInView, setIsInView] = useState(false);
  const imgRef = useRef<HTMLDivElement>(null);
  const [isToggleFavorite, setIsToggleFavorite] = useState(false);
  const [inFavoriteExists, setInFavoriteExists] = useState(false);
  const [selectedBuy, setSelectedBuy] = useState(false);
  const [selectedWarehouse, setSelectedWarehouse] = useState<string>("");
  const [quantity, setQuantity] = useState<number>(1);

  // Мемоизированные значения для оптимизации
  const placeholderImage = useMemo(
    () =>
      "https://avatars.mds.yandex.net/i?id=8e294c809ff68fd77490c5cab8f9a29e_l-4276653-images-thumbs&n=13",
    []
  );

  const productImageUrl = useMemo(
    () =>
      product.image_key === "00000000-0000-0000-0000-000000000000"
        ? placeholderImage
        : `${baseURL}/image/${product.ref_key}?t=${Date.now()}`,
    [product.ref_key, product.image_key, placeholderImage]
  );

  // Обработчики событий
  const handleCardClick = useCallback(() => {
    window.location.href = `/product/${product.ref_key}`;
  }, [product.ref_key]);

  const handleImageError = useCallback(
    (event: React.SyntheticEvent<HTMLImageElement>) => {
      event.currentTarget.onerror = null;
      event.currentTarget.src = placeholderImage;
    },
    [placeholderImage]
  );

  // Эффекты
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
          observer.unobserve(entry.target);
        }
      },
      {
        rootMargin: isMobile ? "50px" : "100px",
        threshold: 0.01,
      }
    );

    const currentRef = imgRef.current;
    if (currentRef) observer.observe(currentRef);

    return () => {
      if (currentRef) observer.unobserve(currentRef);
    };
  }, [isMobile]);

  useEffect(() => {
    if (isInView) {
      setImageUrl(productImageUrl);
    }
  }, [isInView, productImageUrl]);

  useEffect(() => {
    if (!appStore.isLoginIn) {
      const exists = JSON.parse(localStorage.getItem("favorites") || "[]").some(
        (i: Product) => i.ref_key === product.ref_key
      );
      setInFavoriteExists(exists);
    } else {
      setInFavoriteExists(!!product.is_favorite);
    }
  }, [product.ref_key, product.is_favorite, appStore.isLoginIn]);

  // Оптимизированные стили для мобильных устройств
  const mobileStyles = {
    image: "w-[150px] h-[110px] object-contain rounded ml-auto mr-auto",
    title: "text-[14px] mt-[10px] font-[600]",
    price: "text-[16px]",
  };

  const desktopStyles = {
    image: "w-[210px] h-[154px] object-contain rounded mb-[15px]",
    title: "text-[16px] font-semibold tracking-[-2%]",
    price: "text-[18px]",
  };

  const styles = isMobile ? mobileStyles : desktopStyles;

  const availableCurrentCity = () => {
    return _.uniqBy(
      product?.stocks?.filter(
        (i) =>
          i.Stock?.includes(appStore.selectedCity) &&
          i.qnt > 0 &&
          !i.Stock.includes("83.")
      ),
      "Stock"
    );
  };
  const getAvailableQuantity = useCallback(() => {
    if (!selectedWarehouse) return 0;
    const w = availableCurrentCity().find((i) => i.Stock === selectedWarehouse);
    return w?.qnt ?? 0;
  }, [selectedWarehouse]);

  const handleQuantityChange = useCallback(
    (value: number) => {
      if (!selectedWarehouse) return;
      const availableQty = getAvailableQuantity();
      const clamped = Math.min(Math.max(value, 1), availableQty || 1);
      setQuantity(clamped);
    },
    [getAvailableQuantity, selectedWarehouse]
  );

  const onCloseModal = useCallback(() => {
    setSelectedBuy(false);
  }, []);

  const onAddToBasket = useCallback(() => {
    if (!selectedWarehouse) return;
    basketStore.addToBasket({
      product: product,
      warehouse: selectedWarehouse,
      quantity: quantity,
      id: uniqueId("basket_"),
    });
    toast.success("Товар добавлен в корзину", { autoClose: 300 });
    setSelectedBuy(false);
  }, [product, selectedWarehouse, quantity]);
  return (
    <>
    <div
      className={`h-full bg-white flex flex-col cursor-pointer ${className}`}
      ref={imgRef}
      onClick={handleCardClick}
    >
      {/* Блок с изображением и основной информацией */}
      <div className={!isMobile  ? "flex flex-col items-center p-[20px] flex-grow" : "flex flex-col items-center py-[20px] px-[10px] flex-grow" }>
        <div
          className={
            !isMobile ? "w-[210px] mb-[20px]" : "w-full max-w-[185px] py-[12px]"
          }
        >
          <a href={`/product/${product.ref_key}`}>
            <img
              src={imageUrl}
              className={styles.image}
              onError={handleImageError}
              alt={product.name}
              loading="lazy"
              decoding="async"
            />
          </a>
          <a
            className={`text-[#232323] gap-[5px] flex flex-col  leading-[125%] ${styles.title}`}
            title={product.name}
            href={`/product/${product.ref_key}`}
          >
            <span className="font-bold text-[#474A51] break-all">
              {product.brand}
            </span>
            <span className="w-full text-wrap break-all">
              {product.name.length >= 43 ? (
                <>{product.name.slice(0, 43)}...</>
              ) : (
                product.name
              )}
            </span>
          </a>
          <div className="flex mt-[20px] flex-col">
            <ProductInfoRow label="Артикул" value={product.artikul} />
            <ProductInfoRow label="Страна" value={product.country ? product.country?.replace(/\s*\([^)]*\)/g, '').trim() : '-'} />
            {/* <ProductInfoRow label="В упаковке" value="1 шт" /> */}
          </div>
        </div>

        <div
          className={
            !isMobile
              ? "flex flex-col w-full mt-auto"
              : "flex flex-col  mt-auto w-full"
          }
        >
          <div
            className={
              !isMobile
                ? "flex items-center justify-between !w-full px-[0px] pt-[12px] pb-[15px]"
                : "flex  !w-full min-w-[145px] items-center  pt-[12px] pb-[15px] justify-between "
            }
          >
            <div className={isMobile ? "flex flex-col" : "flex flex-col"}>
              <span className={`font-bold ${styles.price} tracking-[-2%]`}>
                ₽ {product.price}
              </span>
              {product.available.has_stock &&
                (product.availableCurrentCity ||
                availableCurrentCity().length > 0 ? (
                  availableCurrentCity().length > 0 && (
                    <span className="text-[#3256F3] font-medium text-[13px] text-nowrap tracking-[-2%] ">
                      В{" "}
                      {getDeclension(availableCurrentCity().length, [
                        "магазине",
                        "магазинах",
                        "магазинах",
                      ])}
                    </span>
                  )
                ) : (
                  <span className="text-[#262626] font-medium text-sm break-all">
                    Другой город
                  </span>
                ))}
            </div>
            <Button
              disabled={isToggleFavorite}
              style="outline"
              className={`!border-[1px] ${isMobile ? '!rounded-[10px]' : "!rounded-[12px]"} !w-[46px] !h-full !min-h-[47px] !p-0 flex items-center justify-center transition-all duration-200 ${
                inFavoriteExists
                  ? "!bg-white !border-[#383181] hover:!bg-[#0000001A] active:!bg-[#ebebeb]"
                  : "!bg-white !border-[#232323] hover:border-[#383181] hover:!bg-[#0000001A] active:!bg-[#ebebeb]"
              }`}
              onClick={async (e) => {
                e.stopPropagation();
                setIsToggleFavorite(true);
                await accountStore.toggleFavorite(product);
                if (appStore.isLoginIn) {
                  setInFavoriteExists((prev) => !prev);
                } else {
                  const exists = JSON.parse(
                    localStorage.getItem("favorites") || "[]"
                  ).some((i: Product) => i.ref_key === product.ref_key);
                  setInFavoriteExists(exists);
                }
                setIsToggleFavorite(false);
              }}
            >
              <span
                className="text-[23px] font-[510] text-[#383181] leading-none"
                style={{
                  fontFamily:
                    "SF Pro, -apple-system, system-ui, BlinkMacSystemFont, sans-serif",
                }}
              >
                {isToggleFavorite ? (
                  <Spinner size="sm" />
                ) : inFavoriteExists ? (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="18"
                    height="17"
                    viewBox="0 0 23 22"
                    fill="none"
                  >
                    <path
                      d="M11.3423 21.0234C11.2075 21.0234 11.0428 20.9785 10.8481 20.8887C10.6535 20.7988 10.4738 20.6978 10.3091 20.5854C8.21273 19.2378 6.38965 17.8228 4.83984 16.3403C3.29753 14.8579 2.10335 13.3306 1.25732 11.7583C0.418783 10.186 -0.000488281 8.60254 -0.000488281 7.00781C-0.000488281 5.97461 0.164225 5.03125 0.493652 4.17773C0.823079 3.31673 1.28353 2.57552 1.875 1.9541C2.46647 1.33268 3.14779 0.853516 3.91895 0.516602C4.69759 0.172201 5.53613 0 6.43457 0C7.55013 0 8.52344 0.284505 9.35449 0.853516C10.1855 1.42253 10.8481 2.16374 11.3423 3.07715C11.8514 2.15625 12.5177 1.41504 13.3413 0.853516C14.1724 0.284505 15.1457 0 16.2612 0C17.1597 0 17.9982 0.172201 18.7769 0.516602C19.5555 0.853516 20.2368 1.33268 20.8208 1.9541C21.4048 2.57552 21.8615 3.31673 22.1909 4.17773C22.5278 5.03125 22.6963 5.97461 22.6963 7.00781C22.6963 8.60254 22.2733 10.186 21.4272 11.7583C20.5812 13.3306 19.387 14.8579 17.8447 16.3403C16.3099 17.8228 14.4943 19.2378 12.3979 20.5854C12.2257 20.6978 12.0423 20.7988 11.8477 20.8887C11.6605 20.9785 11.492 21.0234 11.3423 21.0234Z"
                      fill="#383181"
                    />
                  </svg>
                ) : (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="18"
                    height="17"
                    viewBox="0 0 23 22"
                    fill="none"
                  >
                    <path
                      d="M-0.000488281 7.00781C-0.000488281 5.97461 0.164225 5.03125 0.493652 4.17773C0.823079 3.31673 1.28353 2.57552 1.875 1.9541C2.47396 1.33268 3.17025 0.853516 3.96387 0.516602C4.75749 0.172201 5.611 0 6.52441 0C7.59505 0 8.54215 0.228353 9.36572 0.685059C10.1893 1.13428 10.8481 1.72949 11.3423 2.4707C11.8514 1.72949 12.514 1.13428 13.3301 0.685059C14.1536 0.228353 15.1007 0 16.1714 0C17.0923 0 17.9458 0.172201 18.7319 0.516602C19.5256 0.853516 20.2181 1.33268 20.8096 1.9541C21.401 2.57552 21.8615 3.31673 22.1909 4.17773C22.5278 5.03125 22.6963 5.97461 22.6963 7.00781C22.6963 8.60254 22.2733 10.186 21.4272 11.7583C20.5812 13.3306 19.387 14.8579 17.8447 16.3403C16.3099 17.8228 14.4943 19.2378 12.3979 20.5854C12.2257 20.6978 12.0423 20.7988 11.8477 20.8887C11.6605 20.9785 11.492 21.0234 11.3423 21.0234C11.2075 21.0234 11.0428 20.9785 10.8481 20.8887C10.6535 20.7988 10.4738 20.6978 10.3091 20.5854C8.21273 19.2378 6.38965 17.8228 4.83984 16.3403C3.29753 14.8579 2.10335 13.3306 1.25732 11.7583C0.418783 10.186 -0.000488281 8.60254 -0.000488281 7.00781ZM2.26807 6.99658C2.26807 7.98486 2.50391 8.99186 2.97559 10.0176C3.45475 11.0433 4.10612 12.0578 4.92969 13.061C5.75326 14.0643 6.69661 15.0264 7.75977 15.9473C8.8304 16.8607 9.95719 17.7104 11.1401 18.4966C11.245 18.5789 11.3123 18.6201 11.3423 18.6201C11.3722 18.6201 11.4471 18.5789 11.5669 18.4966C12.7498 17.7104 13.8729 16.8607 14.936 15.9473C15.9992 15.0264 16.9425 14.0643 17.7661 13.061C18.5897 12.0578 19.2373 11.0433 19.709 10.0176C20.1882 8.99186 20.4277 7.98486 20.4277 6.99658C20.4277 6.04574 20.2368 5.21468 19.855 4.50342C19.4731 3.79215 18.9491 3.23812 18.2827 2.84131C17.6239 2.43701 16.8789 2.23486 16.0479 2.23486C15.3665 2.23486 14.7826 2.35465 14.2959 2.59424C13.8167 2.82633 13.4049 3.12207 13.0605 3.48145C12.7161 3.83333 12.4167 4.18522 12.1621 4.53711C11.9974 4.75423 11.8551 4.90771 11.7354 4.99756C11.623 5.0874 11.492 5.13232 11.3423 5.13232C11.2 5.13232 11.069 5.09115 10.9492 5.00879C10.8294 4.91895 10.6872 4.76172 10.5225 4.53711C10.2904 4.17773 10.0021 3.8221 9.65771 3.47021C9.31331 3.11833 8.89404 2.82633 8.3999 2.59424C7.90576 2.35465 7.32178 2.23486 6.64795 2.23486C5.81689 2.23486 5.0682 2.43701 4.40186 2.84131C3.743 3.23812 3.22266 3.79215 2.84082 4.50342C2.45898 5.21468 2.26807 6.04574 2.26807 6.99658Z"
                      fill="#000000"
                    />
                    <path
                      d="M-0.000488281 7.00781C-0.000488281 5.97461 0.164225 5.03125 0.493652 4.17773C0.823079 3.31673 1.28353 2.57552 1.875 1.9541C2.47396 1.33268 3.17025 0.853516 3.96387 0.516602C4.75749 0.172201 5.611 0 6.52441 0C7.59505 0 8.54215 0.228353 9.36572 0.685059C10.1893 1.13428 10.8481 1.72949 11.3423 2.4707C11.8514 1.72949 12.514 1.13428 13.3301 0.685059C14.1536 0.228353 15.1007 0 16.1714 0C17.0923 0 17.9458 0.172201 18.7319 0.516602C19.5256 0.853516 20.2181 1.33268 20.8096 1.9541C21.401 2.57552 21.8615 3.31673 22.1909 4.17773C22.5278 5.03125 22.6963 5.97461 22.6963 7.00781C22.6963 8.60254 22.2733 10.186 21.4272 11.7583C20.5812 13.3306 19.387 14.8579 17.8447 16.3403C16.3099 17.8228 14.4943 19.2378 12.3979 20.5854C12.2257 20.6978 12.0423 20.7988 11.8477 20.8887C11.6605 20.9785 11.492 21.0234 11.3423 21.0234C11.2075 21.0234 11.0428 20.9785 10.8481 20.8887C10.6535 20.7988 10.4738 20.6978 10.3091 20.5854C8.21273 19.2378 6.38965 17.8228 4.83984 16.3403C3.29753 14.8579 2.10335 13.3306 1.25732 11.7583C0.418783 10.186 -0.000488281 8.60254 -0.000488281 7.00781ZM2.26807 6.99658C2.26807 7.98486 2.50391 8.99186 2.97559 10.0176C3.45475 11.0433 4.10612 12.0578 4.92969 13.061C5.75326 14.0643 6.69661 15.0264 7.75977 15.9473C8.8304 16.8607 9.95719 17.7104 11.1401 18.4966C11.245 18.5789 11.3123 18.6201 11.3423 18.6201C11.3722 18.6201 11.4471 18.5789 11.5669 18.4966C12.7498 17.7104 13.8729 16.8607 14.936 15.9473C15.9992 15.0264 16.9425 14.0643 17.7661 13.061C18.5897 12.0578 19.2373 11.0433 19.709 10.0176C20.1882 8.99186 20.4277 7.98486 20.4277 6.99658C20.4277 6.04574 20.2368 5.21468 19.855 4.50342C19.4731 3.79215 18.9491 3.23812 18.2827 2.84131C17.6239 2.43701 16.8789 2.23486 16.0479 2.23486C15.3665 2.23486 14.7826 2.35465 14.2959 2.59424C13.8167 2.82633 13.4049 3.12207 13.0605 3.48145C12.7161 3.83333 12.4167 4.18522 12.1621 4.53711C11.9974 4.75423 11.8551 4.90771 11.7354 4.99756C11.623 5.0874 11.492 5.13232 11.3423 5.13232C11.2 5.13232 11.069 5.09115 10.9492 5.00879C10.8294 4.91895 10.6872 4.76172 10.5225 4.53711C10.2904 4.17773 10.0021 3.8221 9.65771 3.47021C9.31331 3.11833 8.89404 2.82633 8.3999 2.59424C7.90576 2.35465 7.32178 2.23486 6.64795 2.23486C5.81689 2.23486 5.0682 2.43701 4.40186 2.84131C3.743 3.23812 3.22266 3.79215 2.84082 4.50342C2.45898 5.21468 2.26807 6.04574 2.26807 6.99658Z"
                      fill="#383181"
                      fill-opacity="0.5"
                    />
                  </svg>
                )}
              </span>
            </Button>
          </div>
        </div>
        <div className={isMobile ? "w-full" : 'w-full' }>
        <Button
          style="primary"
          className={!isMobile ? "!cursor-pointer w-full h-[45px] items-center gap-[5px] justify-center flex !px-[12px] !py-[10px] !rounded-[12px]" : "!cursor-pointer w-full h-[45px] items-center gap-[5px] justify-center flex  !py-[10px] !px-[15px] !rounded-[12px]"}
          onClick={(e) => {
            e.stopPropagation();
            setSelectedBuy(true);
            setSelectedWarehouse(availableCurrentCity()[0]?.Stock || "");
            setQuantity(1);
          }}
        >
          <div>
            <CartIcon></CartIcon>
          </div>
          <span className={isMobile ? " !text-[14px] !font-semibold" : " !text-[14px] !font-semibold"}>
            В корзину
          </span>
        </Button>
      </div>
      </div>
    </div>

    {/* Add to Cart Modal */}
    <Modal
      isOpen={selectedBuy}
      onClose={onCloseModal}
      size={isMobile ? "md" : "xl"}
      closeButton={false}
      footer={
        <div className={`flex ${isMobile ? "flex-col" : "flex-row"} gap-2`}>
          <Button style="outline" onClick={onCloseModal}>
            Назад
          </Button>
          <Button
            style="primary"
            onClick={(e) => {
              e.stopPropagation();
              onAddToBasket();
            }}
            disabled={!selectedWarehouse}
          >
            В корзину
          </Button>
        </div>
      }
    >
      <form className="p-4 flex flex-col min-h-[50vh] gap-4" onClick={(e) => e.stopPropagation()}>
        <h2 className="text-xl font-bold">Добавить в корзину</h2>
        <div className="flex flex-col gap-2 h-[80px]">
          <label className="text-sm">Выберите точку получения в списке:</label>
      <Select
            options={
              availableCurrentCity().map((i) => ({ value: i.Stock, label: i.Stock })) || []
            }
            value={selectedWarehouse}
            label={
              <span className="font-semibold text-base">
                Магазины в {" "}
                <span className="text-[#007AFF]">г. {appStore.selectedCity}</span>
              </span>
            }
            placeholder="Выберете магазин"
            onChange={(value) => {
              setSelectedWarehouse(String(value));
              setQuantity(1);
            }}
            className="!h-[35px]"
          />
        </div>
        <div className="flex flex-col gap-2">
          <label className="text-sm">Количество:</label>
          <Counter
            min={1}
            max={100}
            value={quantity}
            onChange={handleQuantityChange}
          />
        </div>
      </form>
    </Modal>
    {/* Блок с ценой и кнопкой - всегда внизу */}
    </>
  )
};

// Дочерний компонент для отображения строки информации
const ProductInfoRow: FC<{
  label: string;
  value: string;
  className?: string;
}> = ({ label, value, className }) => (
  <span className={"font-[500] text-[13px] text-[#828282] " + className}>
    {label}: <span className="text-[#232323]">{value}</span>
  </span>
);

// Вынесенная иконка корзины
const CartIcon = () => (
  <svg
    width="21"
    height="21"
    viewBox="0 0 21 21"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M14.4443 5.53223C15.1963 5.53223 15.7705 5.72819 16.167 6.12012C16.5635 6.50749 16.7617 7.07715 16.7617 7.8291V14.8838C16.7617 15.6357 16.5703 16.2054 16.1875 16.5928C15.8047 16.9847 15.2715 17.1807 14.5879 17.1807H8.87988C9.02116 16.9574 9.13965 16.7158 9.23535 16.4561C9.33105 16.2008 9.39714 15.9365 9.43359 15.6631H14.458C14.6859 15.6631 14.8727 15.5856 15.0186 15.4307C15.1644 15.2803 15.2373 15.0479 15.2373 14.7334V7.97949C15.2373 7.66504 15.1576 7.43262 14.998 7.28223C14.8385 7.12728 14.6107 7.0498 14.3145 7.0498H6.67871C6.37793 7.0498 6.14779 7.12728 5.98828 7.28223C5.83333 7.43262 5.75586 7.66504 5.75586 7.97949V10.3721C5.63281 10.3538 5.50749 10.3379 5.37988 10.3242C5.25228 10.306 5.1224 10.2969 4.99023 10.2969C4.86263 10.2969 4.73503 10.3037 4.60742 10.3174C4.48438 10.3311 4.36133 10.3493 4.23828 10.3721V7.8291C4.23828 7.07715 4.43424 6.50749 4.82617 6.12012C5.22266 5.72819 5.79915 5.53223 6.55566 5.53223H14.4443ZM7.56738 5.83984C7.56738 5.28841 7.69499 4.79622 7.9502 4.36328C8.2054 3.92578 8.55176 3.58171 8.98926 3.33105C9.43132 3.07585 9.93262 2.94824 10.4932 2.94824C11.0583 2.94824 11.5618 3.07585 12.0039 3.33105C12.446 3.58171 12.7946 3.92578 13.0498 4.36328C13.305 4.79622 13.4326 5.28841 13.4326 5.83984L11.9902 5.84668C11.9902 5.53678 11.9264 5.26335 11.7988 5.02637C11.6712 4.78483 11.4958 4.5957 11.2725 4.45898C11.0492 4.32227 10.7894 4.25391 10.4932 4.25391C10.2015 4.25391 9.94401 4.32227 9.7207 4.45898C9.4974 4.5957 9.32194 4.78483 9.19434 5.02637C9.07129 5.26335 9.00977 5.53678 9.00977 5.84668L7.56738 5.83984ZM5.00391 18.3975C4.51628 18.3975 4.05827 18.304 3.62988 18.1172C3.2015 17.9303 2.82324 17.6729 2.49512 17.3447C2.17155 17.0212 1.91634 16.6452 1.72949 16.2168C1.54264 15.7884 1.44922 15.3304 1.44922 14.8428C1.44922 14.3506 1.54264 13.8926 1.72949 13.4688C1.91634 13.0404 2.17155 12.6621 2.49512 12.334C2.82324 12.0059 3.2015 11.7507 3.62988 11.5684C4.05827 11.3815 4.51628 11.2881 5.00391 11.2881C5.49154 11.2881 5.94954 11.3815 6.37793 11.5684C6.80632 11.7507 7.18457 12.0059 7.5127 12.334C7.84082 12.6576 8.09603 13.0335 8.27832 13.4619C8.46061 13.8903 8.55176 14.3506 8.55176 14.8428C8.55176 15.3258 8.45833 15.7816 8.27148 16.21C8.08919 16.6383 7.83398 17.0166 7.50586 17.3447C7.17773 17.6729 6.79948 17.9303 6.37109 18.1172C5.94271 18.304 5.48698 18.3975 5.00391 18.3975ZM5.00391 17.1123C5.1543 17.1123 5.27507 17.0667 5.36621 16.9756C5.45736 16.8844 5.50293 16.7637 5.50293 16.6133V15.3418H6.77441C6.92025 15.3418 7.03874 15.2962 7.12988 15.2051C7.22559 15.1139 7.27344 14.9932 7.27344 14.8428C7.27344 14.6924 7.22559 14.5716 7.12988 14.4805C7.03874 14.3893 6.92025 14.3438 6.77441 14.3438H5.50293V13.0791C5.50293 12.9287 5.45736 12.8079 5.36621 12.7168C5.27507 12.6257 5.1543 12.5801 5.00391 12.5801C4.85352 12.5801 4.73275 12.6257 4.6416 12.7168C4.55046 12.8079 4.50488 12.9287 4.50488 13.0791V14.3438H3.2334C3.08301 14.3438 2.96224 14.3893 2.87109 14.4805C2.77995 14.5716 2.73438 14.6924 2.73438 14.8428C2.73438 14.9932 2.77995 15.1139 2.87109 15.2051C2.9668 15.2962 3.08757 15.3418 3.2334 15.3418H4.50488V16.6133C4.50488 16.7637 4.55046 16.8844 4.6416 16.9756C4.73275 17.0667 4.85352 17.1123 5.00391 17.1123Z"
      fill="white"
    />
  </svg>
);

export default ProductCard;
